import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProductImageGallery from './components/ProductImageGallery';
import ProductInfo from './components/ProductInfo';
import ProductPurchase from './components/ProductPurchase';
import CustomerReviews from './components/CustomerReviews';
import RelatedProducts from './components/RelatedProducts';
import SocialShare from './components/SocialShare';
import { useCart } from '../../contexts/CartContext';
import { productsApi } from '../../api';

const ProductDetail = () => {
  const [searchParams] = useSearchParams();
  const productId = searchParams?.get('id');
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const { addToCart } = useCart();
  const [addedToCart, setAddedToCart] = useState(false);


  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const fetchedProduct = await productsApi.get(productId);
        setProduct(fetchedProduct);
        // Assuming the API returns related products or you have a way to fetch them
        // For now, we'll keep the mock related products
        // const fetchedRelated = await productsApi.getRelated(productId);
        // setRelatedProducts(fetchedRelated);
      } catch (error) {
        console.error("Failed to fetch product:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  const handleAddToCart = (productData) => {
    const isSale = product.sale && product.sale.isActive;
    const cartPayload = {
      id: productData.id,
      name: productData.name,
      price: isSale ? parseFloat(product.sale.discountedPrice) : parseFloat(productData.price),
      originalPrice: isSale ? parseFloat(productData.price) : undefined,
      image: productData.urlImage || (productData.additionalImages && productData.additionalImages[0]),
      size: productData.selectedSize || 'Unique',
      color: productData.selectedColor?.name || 'Standard',
      quantity: productData.quantity,
      source: 'From Product Detail'
    };
    addToCart(cartPayload);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const handleAddToWishlist = (productData) => {
    console.log('Adding to wishlist:', productData);
    // In a real app, this would add to wishlist state/context
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-muted-foreground">Loading product details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center space-y-4">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto" />
            <h2 className="text-2xl font-heading font-bold text-foreground">Product Not Found</h2>
            <p className="text-muted-foreground">The product you're looking for doesn't exist.</p>
            <Link to="/collections">
              <Button>
                <Icon name="ArrowLeft" size={16} className="mr-2" />
                Back to Collections
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }
  const isSale = product.sale && product.sale.isActive;
  const displayPrice = isSale ? parseFloat(product.sale.discountedPrice) : parseFloat(product.price);
  const originalPrice = isSale ? parseFloat(product.price) : null;


  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Breadcrumb */}
      <div className="pt-20 pb-4 px-4 lg:px-6">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Link to="/homepage" className="hover:text-accent transition-colors">
              Home
            </Link>
            <Icon name="ChevronRight" size={16} />
            <Link to="/collections" className="hover:text-accent transition-colors">
              Collections
            </Link>
            <Icon name="ChevronRight" size={16} />
            <span className="text-foreground">{product?.name}</span>
          </nav>
        </div>
      </div>
      {/* Main Product Section */}
      <main className="px-4 lg:px-6 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-16">
            {/* Product Images */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <ProductImageGallery product={product} />
            </motion.div>

            {/* Product Purchase Section */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              <ProductPurchase
                product={product}
                onAddToCart={handleAddToCart}
                onAddToWishlist={handleAddToWishlist}
              />
              
              {/* Social Share */}
              <div className="flex items-center justify-between pt-6 border-t border-street">
                <span className="text-sm text-muted-foreground">Share this product:</span>
                <SocialShare product={product} />
              </div>
            </motion.div>
          </div>

          {/* Product Information Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-16"
          >
            <ProductInfo product={product} />
          </motion.div>

          {/* Customer Reviews */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mb-16"
          >
            <CustomerReviews product={product} />
          </motion.div>

          {/* Related Products */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <RelatedProducts products={relatedProducts} />
          </motion.div>
        </div>
      </main>
      {/* Sticky Add to Cart (Mobile) */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-street p-4 z-40">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
          <div className="flex items-baseline space-x-2">
              <span className="font-heading font-bold text-lg text-accent">
                {Number(displayPrice).toFixed(3)} TND
              </span>
              {originalPrice && (
                <span className="text-muted-foreground line-through text-sm">
                  {Number(originalPrice).toFixed(3)} TND
                </span>
              )}
            </div>
          </div>
          <Button
            onClick={() => handleAddToCart(product)}
            className="flex-1"
            disabled={product?.stock === 0}
            loading={addedToCart}
          >
            {addedToCart ? (
              <>
                <Icon name="Check" size={18} className="mr-2" />
                Added!
              </>
            ) : (
              <>
                <Icon name="ShoppingBag" size={18} className="mr-2" />
                Add to Cart
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;